import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { Subject, takeUntil, pairwise, filter } from 'rxjs';
import { LanguageService } from '../../shared/services/language.service';
import { NotificationsService } from '../../shared/services/notifications.service';
import { NotificationDetailDialogComponent } from '../notification-detail-dialog/notification-detail-dialog.component';
import { AuthService } from '../../shared/services/auth.service';
import { NotificationType, NotificationTypeLabels } from 'src/app/models/NotificationType';
import { Notice } from 'src/app/models/Notice';

@Component({
  standalone: false,
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css']
})
export class NotificationsComponent implements OnInit, OnDestroy {

  variables: any;
  notifications: Notice[] = [];
  allTypes: string[] = [];
  displayedColumns: string[] = ['type', 'subject', 'priority', 'date', 'readDate', 'read', 'actions'];

  isLoading = false;
  errorDetected = false;

  pageIndex = 0;
  pageSize = 10;
  totalElements = 0;

  showFiltersValue = true;

  typeOptions: { value: number; label: string; icon?: string }[] | undefined;

  userId: string = '';
  filterType = '';
  filterPriority = '';
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;
  filterRead = 'unread';

  dateValidationError = '';

  errorText = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private languageService: LanguageService,
    private dateAdapter: DateAdapter<Date>,
    private datepickerIntl: MatDatepickerIntl,
    @Inject(NotificationsService) private notificationsService: NotificationsService,
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.languageService.language$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: string) => {
        this.dateAdapter.setLocale(lang || 'es');
        this.setDatepickerIntlLabels(lang || 'es');
      });

    this.languageService.translations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vars: any) => {
        this.variables = vars;
        this.typeOptions = this.buildTypeOptions();

        this.notifications = this.notifications.map((notice: Notice) => {
          const typeMeta = this.getTypeMetaFromEventType(notice.eventType);
          const fallbackLabel = typeMeta?.label || String(notice.eventType);

          return {
            ...notice,
            icon: typeMeta?.icon || '',
            label: this.getTranslatedTypeLabel(typeMeta?.code || String(notice.eventType), fallbackLabel)
          };
        });
      });

    this.authService.getUserData().subscribe(user => {
      if (user) {
        this.userId = user.id
        this.loadNotifications();

        // Reload the table whenever new unread notifications arrive
        this.notificationsService.unreadCount$
          .pipe(
            pairwise(),
            filter(([prev, curr]) => curr > prev),
            takeUntil(this.destroy$)
          )
          .subscribe(() => this.loadNotifications());
      }
    });


    // Prepare dropdown options
    this.typeOptions = this.buildTypeOptions();

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageChange(event: any): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadNotifications();
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.loadNotifications();
  }

  clearFilters(): void {
    this.filterType = '';
    this.filterPriority = '';
    this.filterDateFrom = null;
    this.filterDateTo = null;
    this.filterRead = 'unread';
    this.dateValidationError = '';
    this.pageIndex = 0;
    this.loadNotifications();
  }

  openDetail(notice: Notice): void {
    const dialogRef = this.dialog.open(NotificationDetailDialogComponent, {
      width: '760px',
      maxWidth: '92vw',
      data: notice,
      panelClass: 'profile-menu-panel'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadNotifications();
    });
  }

  goToConfig(): void {
    this.router.navigate(['/notifications/configuration']);
  }

  getPriorityLabel(priority: number): string {
    const labels: { [key: number]: string } = {
      1: this.variables?.priorityVeryHigh || 'Muy alta',
      2: this.variables?.priorityHigh || 'Alta',
      3: this.variables?.priorityMedium || 'Media',
      4: this.variables?.priorityLow || 'Baja',
      5: this.variables?.priorityVeryLow || 'Muy baja'
    };

    return labels[priority] || '-';
  }

  getPriorityClass(priority: number): string {
    return 'priority-' + priority;
  }

  private loadNotifications(): void {
    this.dateValidationError = '';

    const dateFrom = this.formatDateForQuery(this.filterDateFrom);
    const dateTo = this.formatDateForQuery(this.filterDateTo);

    if (dateFrom && dateTo && dateFrom > dateTo) {
      this.dateValidationError = this.variables?.invalidDateRange || 'La fecha de inicio debe ser anterior a la fecha de fin.';
      return;
    }

    this.isLoading = true;
    this.errorDetected = false;

    const query = {
      userId: this.userId, 
      type: this.filterType !== '' ? NotificationTypeLabels[Number(this.filterType) as NotificationType].code : undefined,
      priority: this.filterPriority || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      read: this.filterRead || undefined,
      page: this.pageIndex,
      size: this.pageSize,

    }
    
    this.notificationsService.getNotificationsApi(query).subscribe({
      next: (response: any) => {
        const content = response?.content || response?.items || [];

        this.notifications = content;
        this.totalElements = response?.totalElements ?? content.length;

        const typeValues = (response?.types || content.map((item: Notice) => item.eventType) || [])
          .filter((type: string) => !!type);
        this.allTypes = Array.from(new Set(typeValues));

        //use the NotificationTypeLabels to compare the notification.eventType with the index in the NotificationTypeMeta to get the icon to show in the filter dropdown
        this.notifications = this.notifications.map((notice: Notice) => {
          const typeMeta = this.getTypeMetaFromEventType(notice.eventType);
          const fallbackLabel = typeMeta?.label || String(notice.eventType);

          return {
            ...notice,
            icon: typeMeta?.icon || '',
            label: this.getTranslatedTypeLabel(typeMeta?.code || String(notice.eventType), fallbackLabel)
          };
        });

        this.isLoading = false;
      },
      error: (err) => {
        console.log('Error loading notifications:', err); 
        this.errorDetected = true;
        this.isLoading = false;
      }
    });
  }

  private formatDateForQuery(date: Date | null): string {
    if (!date) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  showFilters() {
    this.showFiltersValue = !this.showFiltersValue;
  }

  private buildTypeOptions(): { value: number; label: string; icon?: string }[] {
    return Object.values(NotificationType)
      .filter(v => typeof v === 'number')
      .map(code => {
        const notificationType = code as NotificationType;
        const typeMeta = NotificationTypeLabels[notificationType];

        return {
          value: code as number,
          label: this.getTranslatedTypeLabel(typeMeta.code, typeMeta.label),
          icon: typeMeta.icon || undefined
        };
      });
  }

  private getTypeMetaFromEventType(eventType: string | number): { code: string; label: string; icon: string } | undefined {
    if (typeof eventType === 'number') {
      return NotificationTypeLabels[eventType as NotificationType];
    }

    return Object.values(NotificationTypeLabels).find(meta => meta.code === String(eventType));
  }

  private getTranslatedTypeLabel(typeCode: string, fallbackLabel: string): string {
    const keyByCode: Record<string, string> = {
      ESTIMATION_TO_CERTIFICATE: 'notificationTypePendingCertification',
      REVIEW_ADDED: 'notificationTypeReviewAdded',
      ESTIMATION_APPROVED: 'notificationTypeApproved',
      ESTIMATION_REJECTED: 'notificationTypeRejected',
      ESTIMATION_TO_FIX: 'notificationTypeToFix'
    };

    const translationKey = keyByCode[typeCode];

    if (!translationKey) {
      return fallbackLabel;
    }

    return this.variables?.[translationKey] || fallbackLabel;
  }

  private setDatepickerIntlLabels(lang: string): void {
    const locale = (lang || 'es').toLowerCase();

    if (locale.startsWith('es')) {
      this.datepickerIntl.openCalendarLabel = 'Abrir calendario';
      this.datepickerIntl.prevMonthLabel = 'Mes anterior';
      this.datepickerIntl.nextMonthLabel = 'Mes siguiente';
      this.datepickerIntl.prevYearLabel = 'A\u00f1o anterior';
      this.datepickerIntl.nextYearLabel = 'A\u00f1o siguiente';
      this.datepickerIntl.prevMultiYearLabel = '21 a\u00f1os anteriores';
      this.datepickerIntl.nextMultiYearLabel = '21 a\u00f1os siguientes';
    } else if (locale.startsWith('pt')) {
      this.datepickerIntl.openCalendarLabel = 'Abrir calend\u00e1rio';
      this.datepickerIntl.prevMonthLabel = 'M\u00eas anterior';
      this.datepickerIntl.nextMonthLabel = 'Pr\u00f3ximo m\u00eas';
      this.datepickerIntl.prevYearLabel = 'Ano anterior';
      this.datepickerIntl.nextYearLabel = 'Pr\u00f3ximo ano';
      this.datepickerIntl.prevMultiYearLabel = '21 anos anteriores';
      this.datepickerIntl.nextMultiYearLabel = 'Pr\u00f3ximos 21 anos';
    } else {
      this.datepickerIntl.openCalendarLabel = 'Open calendar';
      this.datepickerIntl.prevMonthLabel = 'Previous month';
      this.datepickerIntl.nextMonthLabel = 'Next month';
      this.datepickerIntl.prevYearLabel = 'Previous year';
      this.datepickerIntl.nextYearLabel = 'Next year';
      this.datepickerIntl.prevMultiYearLabel = 'Previous 21 years';
      this.datepickerIntl.nextMultiYearLabel = 'Next 21 years';
    }

    this.datepickerIntl.changes.next();
  }

  formatReadDate(value: string | null | undefined): string {
    if (!value) return '';
    const alreadyFormatted = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(value);
    if (alreadyFormatted) return value;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
